import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient } from '@/api/http-client'
import { MutationOptions, queryClient } from '@/api/react-query'
import { customerApplicationsQueryKey } from '@/api/financial/customer/list-customer-applications'
import { CashflowItem } from '@/api/financial/application/read-application'
import { applicationQueryKey } from '@/api/financial/application/read-application'

export type AllowancePreview = {
  summary: {
    monthly_rate: number
    phases: { installments: number; value: number; grace: number; first_due: string }[]
    cet_monthly: number
  }
  cashflow: CashflowItem[]
  limits: {
    min: number
    max: number
    step: number
    accounting_price: number
    rates: { combo: number; solo: number }
  } | null
}

export type AllowanceLimits = {
  min: number
  max: number
  step: number
  grace_periods: number[]
  rates: { combo: number; solo: number }
  accounting_price: number
}

export type AllowanceInput = {
  amount: number
  graduation_date: string
  grace: boolean
  is_combo: boolean
  monthly_tuition?: number
}

export function useAllowanceLimits(graduationDate: string | null) {
  return useQuery({
    queryKey: ['allowance-limits', graduationDate],
    queryFn: async () => {
      const response = await httpClient('financial').get<AllowanceLimits>(
        '/v1/simulation/allowance/limits',
        { params: { graduation_date: graduationDate } },
      )
      return response.data
    },
    enabled: Boolean(graduationDate),
  })
}

export function useAllowancePreview(input: AllowanceInput | null) {
  return useQuery({
    queryKey: ['allowance-preview', input],
    queryFn: async () => {
      const response = await httpClient('financial').post<AllowancePreview>(
        '/v1/simulation/allowance',
        input,
      )
      return response.data
    },
    enabled: Boolean(input && input.amount >= 500),
  })
}

export function useSaveAllowanceSimulation(
  applicationKey: string,
  options?: MutationOptions<AllowancePreview, AllowanceInput>,
) {
  return useMutation({
    mutationFn: async (body: AllowanceInput) => {
      const response = await httpClient('financial').post<AllowancePreview>(
        `/v1/application/${applicationKey}/simulation`,
        body,
      )
      return response.data
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: applicationQueryKey(applicationKey) })
      queryClient.invalidateQueries({ queryKey: customerApplicationsQueryKey })
      options?.onSuccess?.(...args)
    },
  })
}
